import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import Hero from "@/components/site/Hero";
import Marquee from "@/components/site/Marquee";
import ProjectCard from "@/components/site/ProjectCard";
import { Reveal, PageFade } from "@/components/site/motion";
import { SERVICES, TESTIMONIALS } from "@/lib/constants";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function Home() {
  useDocumentTitle("JARE — Versatile Creative Designer");
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState(TESTIMONIALS);
  const [posts, setPosts] = useState([]);
  const location = useLocation();

  useEffect(() => {
    api.get("/portfolio").then((r) => setProjects(r.data.filter((p) => p.is_selected !== false).slice(0, 8))).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/testimonials").then((r) => {
      if (r.data?.length) setTestimonials(r.data);
    }).catch(() => {});
    api.get("/blog").then((r) => setPosts((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  useEffect(() => {
    const target = location.state?.scrollTo || (location.hash ? location.hash.slice(1) : null);
    if (target) {
      const t = setTimeout(() => {
        window.lenis?.scrollTo(`#${target}`, { offset: -60 });
        window.history.replaceState(null, "", "/");
      }, 250);
      return () => clearTimeout(t);
    }
  }, [location.state, location.hash]);

  const spans = [
    "lg:col-span-6",
    "lg:col-span-6",
    "lg:col-span-6",
    "lg:col-span-6",
  ];
  const aspects = ["aspect-video", "aspect-video", "aspect-video", "aspect-video"];

  const sliderRef = useRef(null);
  const sliderPaused = useRef(false);
  const sliderResume = useRef(null);

  useEffect(() => {
    const el = sliderRef.current;
    if (!el || testimonials.length === 0) return;
    let raf;
    let last = performance.now();
    const step = (now) => {
      const dt = now - last;
      last = now;
      if (!sliderPaused.current) {
        el.scrollLeft += dt * 0.045;
        const half = el.scrollWidth / 2;
        if (half > 0 && el.scrollLeft >= half) el.scrollLeft -= half;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [testimonials]);

  const nudgeTestimonials = (dir) => {
    const el = sliderRef.current;
    if (!el) return;
    const card = el.querySelector("figure");
    const w = card ? card.getBoundingClientRect().width + 24 : 420;
    const half = el.scrollWidth / 2;
    while (el.scrollLeft + dir * w >= half) el.scrollLeft -= half;
    while (el.scrollLeft + dir * w < 0) el.scrollLeft += half;
    sliderPaused.current = true;
    el.scrollBy({ left: dir * w, behavior: "smooth" });
    window.clearTimeout(sliderResume.current);
    sliderResume.current = window.setTimeout(() => {
      sliderPaused.current = false;
    }, 3500);
  };

  return (
    <PageFade>
      <Hero />
      <Marquee items={["Graphic Design", "Motion Design", "Video Editing", "UI Design", "Web Development", "Creative Direction"]} />

      {/* Services */}
      <section id="services" className="relative border-t border-white/[0.08] bg-[#0B0B0B]/60 py-28 md:py-36" data-testid="services-section">
        <div className="mx-auto w-[92%] max-w-7xl">
          <Reveal className="mb-14 max-w-2xl">
            <p className="eyebrow mb-5">Services</p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Many disciplines, one standard
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.07} className={i === 0 ? "lg:col-span-2" : ""}>
                <div
                  className="glass glass-hover group h-full rounded-3xl p-8 md:p-10"
                  data-testid={`service-card-${s.title.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-xs tracking-[0.3em] text-neutral-600">{s.n}</span>
                    <ArrowUpRight className="h-5 w-5 text-neutral-600 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-white" />
                  </div>
                  <h3 className="font-display mt-8 text-2xl font-bold tracking-tight text-white">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400">{s.desc}</p>
                  <div className="mt-7 flex flex-wrap gap-2">
                    {s.items.map((it) => (
                      <span key={it} className="rounded-full border border-white/10 px-3 py-1 text-[11px] text-neutral-400">
                        {it}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Work */}
      <section className="mx-auto w-[92%] max-w-7xl py-28 md:py-40" data-testid="selected-work-section">
        <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow mb-5">Selected Work</p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Projects with a pulse
            </h2>
          </div>
          <Link
            to="/work"
            data-testid="view-all-work-link"
            className="group inline-flex items-center gap-2 text-sm font-medium text-neutral-300 transition-colors hover:text-white"
          >
            View all work
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12 lg:gap-8">
          {projects.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08} className={spans[i % spans.length]}>
              <ProjectCard project={p} aspect={aspects[i % aspects.length]} />
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 flex justify-center">
          <Link to="/work" className="btn-primary" data-testid="view-all-work-button">
            View all work <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      {/* Journal */}
      {posts.length > 0 && (
        <section className="bg-[#0c0c0c] py-28 md:py-36" data-testid="home-blog-section">
          <div className="mx-auto w-[92%] max-w-7xl">
          <Reveal className="mb-12 flex flex-wrap items-center justify-between gap-6">
            <div>
              <p className="eyebrow mb-5">Journal</p>
              <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                Latest notes
              </h2>
            </div>
            <Link
              to="/blog"
              data-testid="view-all-blog-link"
              className="group inline-flex items-center gap-2 text-sm font-medium text-neutral-300 transition-colors hover:text-white"
            >
              View all articles
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="home-blog-grid">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <Link to={`/blog/${p.slug}`} className="group block h-full" data-testid={`home-blog-card-${p.slug}`}>
                  <article className="glass glass-hover h-full overflow-hidden rounded-3xl">
                    <div className="overflow-hidden">
                      <img
                        src={mediaUrl(p.featured_image)}
                        alt={p.title}
                        loading="lazy"
                        className="aspect-[16/10] w-full object-cover grayscale-[35%] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                      />
                    </div>
                    <div className="p-6">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{p.category}</p>
                      <h3 className="font-display mt-2.5 line-clamp-2 text-lg font-bold tracking-tight text-white">
                        {p.title}
                      </h3>
                      <p className="mt-2.5 line-clamp-2 text-sm leading-relaxed text-neutral-400">{p.excerpt}</p>
                    </div>
                  </article>
                </Link>
              </Reveal>
            ))}
          </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="bg-[#070708] py-28 md:py-36" data-testid="testimonials-section">
        <div className="mx-auto w-[92%] max-w-7xl">
          <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow mb-5">Words from clients</p>
              <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                Trusted with taste
              </h2>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => nudgeTestimonials(-1)}
                data-testid="testimonial-prev"
                aria-label="Previous testimonials"
                className="glass glass-hover flex h-11 w-11 items-center justify-center rounded-full text-neutral-300 transition-colors hover:text-white"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => nudgeTestimonials(1)}
                data-testid="testimonial-next"
                aria-label="Next testimonials"
                className="glass glass-hover flex h-11 w-11 items-center justify-center rounded-full text-neutral-300 transition-colors hover:text-white"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </Reveal>
        </div>
        <div className="relative" data-testid="testimonials-slider">
          <div
            ref={sliderRef}
            onPointerEnter={() => (sliderPaused.current = true)}
            onPointerLeave={() => (sliderPaused.current = false)}
            className="no-scrollbar flex w-full overflow-x-auto"
          >
            {[...testimonials, ...testimonials].map((t, i) => (
              <figure
                key={`${t.id || t.name}-${i}`}
                aria-hidden={i >= testimonials.length}
                className="glass glass-hover mr-6 w-[85vw] shrink-0 rounded-3xl p-8 sm:w-[400px] lg:w-[calc((min(92vw,80rem)-3rem)/3)]"
                data-testid={i < testimonials.length ? `testimonial-${i}` : undefined}
              >
                <blockquote className="text-base leading-relaxed text-neutral-300">
                  “{t.text || t.quote}”
                </blockquote>
                <figcaption className="mt-8 flex items-center gap-3 border-t border-white/[0.08] pt-5">
                  {t.photo ? (
                    <img src={mediaUrl(t.photo)} alt={t.name} className="h-11 w-11 rounded-xl border border-white/10 object-cover" />
                  ) : (
                    <span className="glass flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-display text-sm font-semibold text-white">
                      {(t.name || "?")[0]}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white">{t.name}</p>
                    <p className="mt-0.5 truncate text-xs text-neutral-500">{t.position || t.role}</p>
                    {t.project_slug && (
                      <Link
                        to={`/work/${t.project_slug}`}
                        data-testid={i < testimonials.length ? `testimonial-work-link-${i}` : undefined}
                        className="mt-1.5 inline-flex items-center gap-1 text-xs text-neutral-400 underline decoration-white/20 underline-offset-4 transition-colors hover:text-white"
                      >
                        View related work <ArrowUpRight className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-14 bg-gradient-to-r from-[#070708] to-transparent md:w-28" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-14 bg-gradient-to-l from-[#070708] to-transparent md:w-28" />
        </div>
      </section>
    </PageFade>
  );
}
